import { useEffect } from "react";
import { MapPin } from "lucide-react";
import { useIntake, IntakeShell } from "./IntakeShell";
import { useGeo } from "../../lib/geo";
import { CTA_INSURANCE } from "../../lib/ctaVocabulary";

/**
 * Lightweight auto-insurance intake.
 *
 * Iter-196.PP — Form-friction simplification (2026-02-16). Reduced from
 * 3 fields → 2 fields. Affiliate partners collect the rest of the
 * profile themselves on their own intake screens; collecting it twice
 * was friction without conversion lift.
 *   - ZIP code (required — partner-universal routing anchor)
 *   - Currently insured? (optional — drives switcher vs. new-buyer
 *     routing when provided, but no longer blocks submission)
 *
 * Retired in iter-196.PP:
 *   - vehicle_owned (Yes/No) — affiliates re-ask this on their flow.
 */
const DEFAULTS = {
  zip_code: "",
  currently_insured: null,
};

function validate(values) {
  if (!/^\d{5}$/.test(values.zip_code || "")) return "Enter a valid 5-digit ZIP";
  return null;
}

export default function IntakeInsurance() {
  const state = useIntake({
    category: "insurance",
    defaults: DEFAULTS,
    validate,
    contextLine:
      "Compare rates from trusted insurance marketplaces and carriers. Most comparisons take about 2 minutes.",
  });
  const { values, setField } = state;
  const { geo } = useGeo();

  // Auto-fill ZIP from geo if user hasn't typed.
  useEffect(() => {
    if (!geo?.zip || values.zip_code) return;
    if (/^\d{5}$/.test(String(geo.zip))) setField("zip_code", String(geo.zip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo]);

  return (
    <IntakeShell
      category="insurance"
      state={state}
      submitLabel={CTA_INSURANCE}
    >
      <FieldZip
        value={values.zip_code}
        onChange={(v) => setField("zip_code", v)}
        geoLabel={
          geo?.city && geo?.region ? `${geo.city}, ${geo.region}` : null
        }
        autoFilled={!!values.zip_code && values.zip_code === String(geo?.zip || "")}
      />
      <YesNoField
        label="Are you currently insured? (optional)"
        value={values.currently_insured}
        onChange={(v) => setField("currently_insured", v)}
        testid="intake-insurance-current"
      />
    </IntakeShell>
  );
}

// -------- Shared field primitives (intentionally co-located — these are
// not used outside intake forms, so no need to elevate to a shared module). --

export function FieldZip({ value, onChange, geoLabel, autoFilled }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center justify-between gap-2">
        <span>ZIP Code</span>
        {autoFilled && geoLabel && (
          <span
            data-testid="intake-zip-autofill"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981] normal-case"
          >
            <MapPin className="w-3 h-3" />
            <span className="normal-case tracking-normal">
              Detected · {geoLabel}
            </span>
          </span>
        )}
      </label>
      <input
        type="text"
        inputMode="numeric"
        pattern="\d{5}"
        maxLength={5}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder="90210"
        data-testid="intake-zip"
        aria-label="ZIP code"
        autoComplete="postal-code"
        enterKeyHint="next"
        className="w-full h-14 px-4 bg-white border border-slate-300 focus:border-slate-950 outline-none text-2xl font-display font-bold tracking-tight transition-colors"
      />
    </div>
  );
}

export function YesNoField({ label, value, onChange, testid, helpText }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
        {label}
      </p>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-2">
        {[
          { id: true, label: "Yes" },
          { id: false, label: "No" },
        ].map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={String(opt.id)}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.id)}
              data-testid={`${testid}-${opt.label.toLowerCase()}`}
              className={`h-12 text-sm font-bold transition-colors border ${
                active
                  ? "bg-slate-950 text-white border-slate-950"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-950"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {helpText && (
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{helpText}</p>
      )}
    </div>
  );
}
